import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

type VerificationState = 'verifying' | 'success' | 'error' | 'timeout';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerificationState>('verifying');
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let redirectTimer: NodeJS.Timeout;
    let isMounted = true;

    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      let paymentIntentId = searchParams.get('payment_intent');
      const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
      const bookingIdParam = searchParams.get('booking_id');

      // If payment_intent is not in URL but we have client_secret, extract it
      // Client secret format: pi_xxxxx_secret_xxxxx
      if (!paymentIntentId && paymentIntentClientSecret) {
        const parts = paymentIntentClientSecret.split('_secret_');
        if (parts.length > 0 && parts[0].startsWith('pi_')) {
          paymentIntentId = parts[0];
          console.log('Extracted payment_intent from client_secret:', paymentIntentId);
        }
      }

      if ((!sessionId && !paymentIntentId) || !bookingIdParam) {
        if (isMounted) {
          setState('error');
          setErrorMessage('Invalid payment verification link');
          toast.error('Invalid payment verification link');
        }
        return;
      }

      if (isMounted) {
        setBookingId(bookingIdParam);
      }

      // Set timeout (10 seconds)
      timeoutId = setTimeout(() => {
        if (isMounted) {
          setState((currentState) => {
            if (currentState === 'verifying') {
              toast.warning('Payment verification is taking longer than expected');
              return 'timeout';
            }
            return currentState;
          });
        }
      }, 10000);

      try {
        // Call edge function to verify payment and update booking status
        const { data, error } = await supabase.functions.invoke('complete-booking-payment', {
          body: {
            sessionId: sessionId || undefined,
            paymentIntentId: paymentIntentId || undefined,
          }
        });

        if (!isMounted) return;

        clearTimeout(timeoutId);

        if (error) {
          console.error('Payment verification error:', error);
          setState('error');
          setErrorMessage(error.message || 'Failed to verify payment');
          toast.error(error.message || 'Failed to verify payment');
          return;
        }
        
        if (!data?.success) {
          setState('error');
          setErrorMessage('Failed to complete payment verification');
          toast.error('Failed to complete payment');
          return;
        }

        // Payment verified successfully
        setState('success');

        // Auto-redirect after 3 seconds
        redirectTimer = setTimeout(() => {
          if (isMounted) {
            navigate(`/book/confirmation?booking_id=${bookingIdParam}`, { replace: true });
          }
        }, 3000);
      } catch (error: any) {
        if (!isMounted) return;
        clearTimeout(timeoutId);
        console.error('Error verifying payment:', error);
        setState('error');
        setErrorMessage(error.message || 'An unexpected error occurred');
        toast.error('Failed to verify payment. Please contact support.');
      }
    };

    verifyPayment();

    // Cleanup function
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [searchParams, navigate]);

  const handleContinue = () => {
    if (bookingId) {
      navigate(`/book/confirmation?booking_id=${bookingId}`, { replace: true });
    } else {
      navigate('/dashboard');
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleContactSupport = () => {
    // TODO: Implement contact support flow
    toast.info('Please contact support with your booking ID: ' + bookingId);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 text-center space-y-4">
          {state === 'verifying' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Verifying Payment</h2>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Payment Successful!</h2>
              <p className="text-muted-foreground mb-4">
                Your payment has been confirmed. Redirecting to confirmation page...
              </p>
              <Button onClick={handleContinue} className="w-full" size="lg">
                Continue to Confirmation
              </Button>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
              <h2 className="text-xl font-semibold">Verification Failed</h2>
              <Alert variant="destructive" className="text-left">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Button onClick={handleRetry} variant="default" className="w-full" size="lg">
                  Try Again
                </Button>
                <Button onClick={handleContactSupport} variant="outline" className="w-full" size="lg">
                  Contact Support
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  variant="ghost" 
                  className="w-full" 
                  size="lg"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {state === 'timeout' && (
            <>
              <div className="h-16 w-16 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="h-10 w-10 text-orange-500" />
              </div>
              <h2 className="text-xl font-semibold">Verification Taking Longer</h2>
              <p className="text-muted-foreground mb-4">
                Payment verification is taking longer than expected. This may be normal. 
                You can check your booking status in the dashboard or try verifying manually.
              </p>
              <div className="space-y-2">
                <Button onClick={handleContinue} variant="default" className="w-full" size="lg">
                  Continue Anyway
                </Button>
                <Button onClick={handleRetry} variant="outline" className="w-full" size="lg">
                  Retry Verification
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  variant="ghost" 
                  className="w-full" 
                  size="lg"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
